import { call, put, select, takeEvery } from "redux-saga/effects";
import actions from "../actions";
import {
  changeUserPassword,
  getInvitation,
  completeInvitation,
} from "../api/signin";
import { logout } from "../api/auth";
import { clearCachedToken } from "../utils/securerequest";
import { getSignedinState } from "../selectors";
import { redirectToAuthPortal } from "../utils/authPortal";

// Axios errors carry the request body in `config.data`, which for these
// requests holds passwords. Keep only what is safe to store, log or report.
const sanitizeApiError = (e) => ({
  message: e?.response?.data?.error || e?.message,
  code: e?.response?.data?.code,
  status: e?.response?.status,
});

function* signoutUser() {
  const signedInState = yield select(getSignedinState);
  // Only perform the full logout (POST /auth/logout + clear cookies) if the
  // user was previously authenticated. During invitation/setup flows,
  // signedInState is null because the user hasn't completed authentication
  // yet; logging out then would clear session state later steps depend on.
  const didLogout = signedInState === true;
  try {
    if (didLogout) {
      yield call(logout);
    }
    yield call(clearCachedToken);
  } catch (e) {
    yield put(actions.logError({ errorMessage: e, errorType: "API" }));
  } finally {
    yield put(actions.setSignedinState(false));
    yield put(actions.clearBasicDetails());
    // Redirect straight to the auth portal once the session is torn down, so
    // the user isn't left on a loading dashboard waiting for the next
    // /auth/me. No-ops on public in-app routes (invitation/setup/etc.).
    if (didLogout) {
      redirectToAuthPortal();
    }
  }
}

function* changeUserPasswordAsync({ payload }) {
  try {
    yield call(changeUserPassword, payload);
    yield put(actions.clearPasswordChange());
    yield put(actions.resetPasswordSuccess());
  } catch (e) {
    const error = sanitizeApiError(e);
    yield put(actions.resetPasswordError(error));
    yield put(actions.logError({ errorMessage: error, errorType: "API" }));
    console.log(error);
  }
}

function* getInvitationAsync({ payload }) {
  try {
    const invitation = yield call(getInvitation, payload);
    yield put(actions.getLoginInvitationSuccess(invitation.data));
  } catch (e) {
    if (e.response && e.response.data) {
      yield put(actions.getLoginInvitationFail(e.response.data.error));
    }
    yield put(actions.logError({ errorMessage: e, errorType: "API" }));
    console.error(e);
  }
}

function* completeInvitationAsync({ payload }) {
  try {
    // Backend-owned setup completion (no client-side Cognito): an-api sets the
    // password, establishes the session cookie, and marks the invitation used.
    const result = yield call(completeInvitation, payload);
    const data = result?.data || {};

    // Only proceed as logged-in if the backend actually established the session
    // cookie. Otherwise we'd fire authenticated requests with no cookie and get a
    // cascade of 401s instead of a clear failure.
    if (!data.sessionEstablished) {
      const reason = data.sessionError || "session was not established";
      console.error(`[completeInvitation] Password set but no session: ${reason}`);
      yield put(
        actions.completeInvitationFail(
          "Your password was set, but we couldn't sign you in automatically. Please log in with your new password.",
        ),
      );
      yield put(
        actions.logError({
          errorMessage: `completeInvitation: password set but session not established — ${reason}`,
          errorType: "API",
        }),
      );
      return;
    }

    if (data.email) {
      yield put(actions.setBasicDetails({ email: data.email, groups: [] }));
    }
    // Session is established server-side — load the user and mark signed in.
    yield put(actions.getUser());
    yield put(actions.setSignedinState(true));
    yield put(actions.completeInvitationSuccess(data));
  } catch (e) {
    const error = sanitizeApiError(e);
    yield put(actions.completeInvitationFail(e?.response?.data?.error || "Failed to set password"));
    yield put(actions.logError({ errorMessage: error, errorType: "API" }));
    console.error(error);
  }
}

export default function* signinSaga() {
  yield takeEvery(actions.SIGN_OUT, signoutUser);
  yield takeEvery(actions.CHANGE_USER_PASSWORD, changeUserPasswordAsync);
  yield takeEvery(actions.GET_INVITATION, getInvitationAsync);
  yield takeEvery(actions.COMPLETE_INVITATION, completeInvitationAsync);
}
